// Route-config factories for the Circle / Network / Space broadcast
// new / detail / edit routes. Each route fetches the parent group's
// detail (name) and renders one of three broadcast components inside
// `GroupAdminShell`.
//
// The kind-specific detail fetcher is hidden behind `useGroupDetail`,
// which projects the per-kind response onto a uniform
// `{ name, isLoading }` shape.

import { getRouteApi, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { DelayedSkeleton, PageHeader, PageHeaderSkeleton, Stack } from '@leanin/ui';
import { isUuid } from '@leanin/shared';

import { groupKeys } from '@/lib/queries/groups';
import { spacesKeys } from '@/lib/queries/spaces';
import { GroupAdminShell } from '@/components/group/admin/GroupAdminShell';
import type { GroupAdminKind } from '@/components/group/admin/sections';
import { BroadcastComposerForm } from '@/components/broadcasts/BroadcastComposerForm';
import { BroadcastDetailView } from '@/components/broadcasts/BroadcastDetailView';
import { EditBroadcastPage } from '@/components/broadcasts/EditBroadcastPage';

// Per-kind URL paths the broadcast pages link back to.
const BACK_TO_LIST = (kind: GroupAdminKind, id: string): string => {
  switch (kind) {
    case 'circle':
      return `/circles/${id}/admin?section=broadcasts`;
    case 'network':
      return `/networks/${id}/admin?section=broadcasts`;
    case 'space':
      return `/groups/${id}/admin?section=broadcasts`;
  }
};

// Fallback group name used while detail is loading. Mirrors what each
// per-kind route had inlined.
const FALLBACK_GROUP_NAME = (kind: GroupAdminKind): string => {
  switch (kind) {
    case 'circle':
      return 'this circle';
    case 'network':
      return 'this network';
    case 'space':
      return 'this group';
  }
};

// Per-kind detail hooks projected onto the unified `{ name, isLoading }`
// shape consumed by the broadcast routes. Each is an independent React
// hook so that within a single route function the hook order is stable
// — `useGroupDetail` below resolves to a single one of these per route
// at factory-construction time.
function useCircleNameById(
  id: string,
): { readonly name: string | null; readonly isLoading: boolean } {
  const q = useQuery(groupKeys.circle.detail(id));
  const detail = q.data;
  const circle =
    detail !== undefined && detail.access !== 'visitor' ? detail.circle : null;
  return { name: circle?.name ?? null, isLoading: q.isLoading };
}

function useNetworkNameById(
  id: string,
): { readonly name: string | null; readonly isLoading: boolean } {
  const q = useQuery(groupKeys.network.detail(id));
  return { name: q.data?.name ?? null, isLoading: q.isLoading };
}

function useSpaceNameById(
  id: string,
): { readonly name: string | null; readonly isLoading: boolean } {
  const q = useQuery(spacesKeys.detail(id));
  return { name: q.data?.name ?? null, isLoading: q.isLoading };
}

/**
 * Resolve the per-kind name-fetcher hook for use inside a route
 * component. Called once at module-evaluation / route-factory time, then
 * the chosen hook is called every render — preserving stable hook order.
 */
function resolveDetailHook(
  kind: GroupAdminKind,
): (id: string) => { readonly name: string | null; readonly isLoading: boolean } {
  if (kind === 'circle') return useCircleNameById;
  if (kind === 'network') return useNetworkNameById;
  return useSpaceNameById;
}

// ──────────────────────────────────────────────────────────────────────
// New broadcast route
// ──────────────────────────────────────────────────────────────────────

export type CreateGroupBroadcastNewRouteOpts = {
  readonly kind: GroupAdminKind;
  readonly fromId: string;
};

export function createGroupBroadcastNewRoute(
  opts: CreateGroupBroadcastNewRouteOpts,
) {
  const { kind, fromId } = opts;
  const useName = resolveDetailHook(kind);

  function NewBroadcastRouteComponent() {
    const routeApi = getRouteApi(fromId as never);
    const { id } = routeApi.useParams() as { id: string };
    const { name } = useName(id);
    const backHref = BACK_TO_LIST(kind, id);

    return (
      <GroupAdminShell id={id} kind={kind} section="broadcasts">
        <Stack gap="xl">
          <DelayedSkeleton
            isLoading={name === null}
            fallback={<PageHeaderSkeleton withSubtitle />}
          >
            {name !== null ? (
              <PageHeader
                back={{ href: backHref, label: `${name} · Broadcasts` }}
                title="New broadcast"
                subtitle={`Email all members of ${name}. Schedule for later or send now.`}
              />
            ) : null}
          </DelayedSkeleton>
          {name !== null ? (
            <BroadcastComposerForm
              audience={{
                mode: 'group',
                kind,
                groupId: id,
                groupName: name,
              }}
            />
          ) : null}
        </Stack>
      </GroupAdminShell>
    );
  }

  return { component: NewBroadcastRouteComponent };
}

// ──────────────────────────────────────────────────────────────────────
// Broadcast detail route
// ──────────────────────────────────────────────────────────────────────

export type CreateGroupBroadcastDetailRouteOpts = {
  readonly kind: GroupAdminKind;
  readonly fromId: string;
};

export function createGroupBroadcastDetailRoute(
  opts: CreateGroupBroadcastDetailRouteOpts,
) {
  const { kind, fromId } = opts;

  const beforeLoad = ({ params }: { params: { broadcastId: string } }) => {
    if (!isUuid(params.broadcastId)) throw notFound();
  };

  function DetailBroadcastRouteComponent() {
    const routeApi = getRouteApi(fromId as never);
    const { id, broadcastId } = routeApi.useParams() as {
      id: string;
      broadcastId: string;
    };
    return (
      <GroupAdminShell id={id} kind={kind} section="broadcasts">
        <BroadcastDetailView
          scope={{ kind: 'group', groupKind: kind, groupId: id }}
          broadcastId={broadcastId}
        />
      </GroupAdminShell>
    );
  }

  return { beforeLoad, component: DetailBroadcastRouteComponent };
}

// ──────────────────────────────────────────────────────────────────────
// Broadcast edit route
// ──────────────────────────────────────────────────────────────────────

export type CreateGroupBroadcastEditRouteOpts = {
  readonly kind: GroupAdminKind;
  readonly fromId: string;
};

export function createGroupBroadcastEditRoute(
  opts: CreateGroupBroadcastEditRouteOpts,
) {
  const { kind, fromId } = opts;
  const useName = resolveDetailHook(kind);

  const beforeLoad = ({ params }: { params: { broadcastId: string } }) => {
    if (!isUuid(params.broadcastId)) throw notFound();
  };

  function EditBroadcastRouteComponent() {
    const routeApi = getRouteApi(fromId as never);
    const { id, broadcastId } = routeApi.useParams() as {
      id: string;
      broadcastId: string;
    };
    const { name } = useName(id);
    const groupName = name ?? FALLBACK_GROUP_NAME(kind);

    return (
      <GroupAdminShell id={id} kind={kind} section="broadcasts">
        <EditBroadcastPage
          scope={{ kind: 'group', groupKind: kind, groupId: id }}
          broadcastId={broadcastId}
          audienceFromScope={{
            mode: 'group',
            kind,
            groupId: id,
            groupName,
          }}
        />
      </GroupAdminShell>
    );
  }

  return { beforeLoad, component: EditBroadcastRouteComponent };
}
